/**
 * OAuth Mutation Hooks
 * Mutations for OAuth setup and integration with Google and Microsoft
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../useAuth.js";
import { apiGet } from "../../../lib/api-utils.js";
import { ENDPOINTS } from "../config.js";
import { queryKeys } from "../query-keys.js";
import type { OAuthSetupParams } from "../types.js";
import { buildQueryParams, getAuthTokenWithRetry } from "../utils.js";

/**
 * Google OAuth Setup
 * Handles OAuth callback and token exchange for Google Calendar
 */
export const useGoogleOAuthSetup = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      code,
      code_verifier,
      isSecondAccount,
    }: OAuthSetupParams) => {
      // Get token with retry logic (more reliable after OAuth redirect)
      const token = await getAuthTokenWithRetry(getAuthToken);

      const queryParams = buildQueryParams({
        code,
        code_verifier,
        isSecondAccount: isSecondAccount ? "true" : undefined,
      });

      const response = await apiGet(
        ENDPOINTS.calendar.google,
        token,
        queryParams
      );

      return response;
    },
    onSuccess: () => {
      // Invalidate calendar queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.google(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.combined(),
      });
    },
  });
};

/**
 * Microsoft OAuth Setup
 * Handles OAuth callback and token exchange for Microsoft Calendar
 */
export const useMicrosoftOAuthSetup = () => {
  const { getAuthToken } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      code,
      code_verifier,
      isSecondAccount,
    }: OAuthSetupParams) => {
      // Get token with retry logic (more reliable after OAuth redirect)
      const token = await getAuthTokenWithRetry(getAuthToken);

      const queryParams = buildQueryParams({
        code,
        code_verifier,
        isSecondAccount: isSecondAccount ? "true" : undefined,
      });

      const response = await apiGet(
        ENDPOINTS.calendar.microsoft,
        token,
        queryParams
      );

      return response;
    },
    onSuccess: () => {
      // Invalidate calendar queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.microsoft(),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.calendar.combined(),
      });
    },
  });
};
